import {t} from '@github-ui/swp-core/lib/utils/localization'
import type {SupportedLocale} from '@github-ui/swp-core/lib/utils/localization'
import type {BannerDescriptor, Universe26Artwork} from './marketing-global-banner.types'

// `/home` also serves the homepage.
const HOMEPAGE_PATHS = ['/', '/home']

const enterpriseCopilotCopy: Record<SupportedLocale, Pick<BannerDescriptor, 'title' | 'link_label'>> = {
  en: {
    title: 'GitHub Enterprise now includes access to GitHub Copilot.',
    link_label: 'Learn more',
  },
  fr: {
    title: 'GitHub Enterprise inclut désormais l’accès à GitHub Copilot',
    link_label: 'En savoir plus',
  },
  de: {
    title: 'GitHub Enterprise umfasst jetzt auch den Zugriff auf GitHub Copilot.',
    link_label: 'Mehr erfahren',
  },
  ja: {
    title: 'GitHub Enterprise には、GitHub Copilot へのアクセス機能が追加されました。',
    link_label: '詳しくはこちら',
  },
  ko: {
    title: '이제 GitHub Enterprise에 GitHub Copilot 액세스가 포함됩니다.',
    link_label: '자세히 알아보기',
  },
  pt: {
    title: 'O GitHub Enterprise agora inclui acesso ao GitHub Copilot.',
    link_label: 'Saiba mais',
  },
  es: {
    title: 'GitHub Enterprise ahora incluye GitHub Copilot.',
    link_label: 'Obtener más información',
  },
}

const universe26Link = (uid: string) =>
  `https://githubuniverse.com/?utm_source=github&utm_medium=banner&utm_campaign=${uid}`

const UNIVERSE26_RECAP_LINK =
  'https://github.com/events/universe/recap?utm_source=global-banner-universe-recap&utm_medium=web&utm_campaign=universe-2026'

// Banners that link to the recap page shouldn't render on it.
const UNIVERSE26_RECAP_EXCLUDED_PATHS = [...HOMEPAGE_PATHS, '/events/universe/recap']

function universe26Banner({
  uid,
  link = universe26Link(uid),
  feature_flag,
  artwork,
  ...rest
}: Pick<
  BannerDescriptor,
  'uid' | 'title' | 'link_label' | 'feature_flag' | 'countdown' | 'live_dot' | 'live_highlight' | 'excluded_paths'
> & {
  link?: string
  artwork: Universe26Artwork
}): BannerDescriptor {
  return {
    uid,
    link,
    // The on-demand artwork is drawn for a black banner.
    mode: artwork === 'on-d' ? 'dark' : 'light',
    custom_design: 'universe_26',
    artwork,
    feature_flag,
    ...rest,
  }
}

/**
 * To add a banner, append a descriptor here and register its `feature_flag` in the `jsFeatureFlags`
 * allowlist in `@github-ui/feature-flags`, or it will never be enabled client-side.
 *
 * Universe 26 campaigns are listed newest first, so a later campaign wins if an earlier flag is left on.
 */
export function getBannerRegistry(locale?: SupportedLocale): BannerDescriptor[] {
  return [
    {
      uid: 'enterprise_copilot',
      ...enterpriseCopilotCopy[locale ?? 'en'],
      link: 'https://gh.io/ghe-with-cp',
      mode: 'dark',
      allowed_paths: ['/features/copilot', '/features/copilot/plans'],
      feature_flag: 'site_global_banner_ghe_copilot',
    },
    {
      uid: 'technology_partner_of_the_year_2026',
      title: 'Congratulations to Sentry, GitHub’s 2026 Technology Partner of the Year! 🏆',
      link: 'https://www.linkedin.com/posts/github-githubpartners-share-7521304299692535808-TWcw/',
      link_label: 'See the announcement',
      mode: 'dark',
      allowed_paths: ['/partners/technology-partners'],
      feature_flag: 'site_global_banner_technology_partner_of_the_year_2026',
    },
    universe26Banner({
      uid: 'gb_post_on-d_uni26',
      title: 'Catch up on all that was announced.',
      link: UNIVERSE26_RECAP_LINK,
      link_label: "See what's new",
      feature_flag: 'universe26_banners_post_on-d',
      artwork: 'on-d',
      excluded_paths: UNIVERSE26_RECAP_EXCLUDED_PATHS,
    }),
    universe26Banner({
      uid: 'gb_day2_key_uni26',
      title: 'Day 2: Keynote now streaming!',
      live_dot: true,
      link_label: 'Watch now',
      feature_flag: 'universe26_banners_day2_key',
      artwork: 'keyn',
      excluded_paths: HOMEPAGE_PATHS,
    }),
    universe26Banner({
      uid: 'gb_on-d_uni26',
      title: 'Catch up on all that was announced.',
      link: UNIVERSE26_RECAP_LINK,
      link_label: "See what's new",
      feature_flag: 'universe26_banners_on-d',
      artwork: 'on-d',
      excluded_paths: UNIVERSE26_RECAP_EXCLUDED_PATHS,
    }),
    universe26Banner({
      uid: 'gb_keyn_uni26',
      title: 'Opening Keynote is now LIVE!',
      live_highlight: 'LIVE',
      link_label: 'Watch now',
      feature_flag: 'universe26_banners_keyn',
      artwork: 'keyn',
    }),
    // Same banner as gb_tom_uni26 with a new uid so users who dismissed it see it again.
    universe26Banner({
      uid: 'gb_tom_refresh_uni26',
      title: 'Day 1: Opening Keynote begins in',
      link_label: 'Register virtually',
      feature_flag: 'universe26_banners_tom_refresh',
      artwork: 'tom',
      countdown: {
        // 9am Pacific on Oct 28, 2026 (PDT, UTC-7).
        to: '2026-10-28T09:00:00-07:00',
        accessible_label: 'Day 1: Opening Keynote begins October 28 at 9 AM Pacific Time',
      },
    }),
    universe26Banner({
      uid: 'gb_tom_uni26',
      title: 'Day 1: Opening Keynote begins in',
      link_label: 'Register virtually',
      feature_flag: 'universe26_banners_tom',
      artwork: 'tom',
      countdown: {
        // 9am Pacific on Oct 28, 2026 (PDT, UTC-7).
        to: '2026-10-28T09:00:00-07:00',
        accessible_label: 'Day 1: Opening Keynote begins October 28 at 9 AM Pacific Time',
      },
    }),
    {
      uid: 'gb_virt_uni26',
      title: 'Join Universe virtually, from anywhere.',
      link: universe26Link('gb_virt_uni26'),
      link_label: 'Save your seat',
      mode: 'light',
      custom_design: 'universe_26',
      feature_flag: 'universe26_banners_virt',
    },
    {
      uid: 'desktop_copilot_app',
      title: 'Looking for an agent-driven desktop experience?',
      link: 'https://github.com/features/ai/github-app?utm_source=github&utm_medium=banner&utm_campaign=desktop_copilot_app',
      link_label: 'Download the GitHub Copilot app',
      mode: 'dark',
      allowed_paths: ['/apps/desktop'],
      feature_flag: 'site_banner_desktop_copilot_app',
    },
    {
      uid: 'dev_days_attendee',
      title: t(
        'Dev Days are here! Join an in-person event near you to learn more about the GitHub Copilot app and Copilot CLI with your community.',
        locale,
      ),
      link: 'https://gh.io/join-dev-days?utm_source=github&utm_medium=banner&utm_campaign=dev_days_attendee',
      link_label: 'Find an event',
      mode: 'dark',
      allowed_paths: [
        '/features/copilot',
        '/features/copilot/cli',
        '/features/copilot/plans',
        '/features/ai/github-app',
        '/features',
        '/resources',
      ],
      feature_flag: 'site_global_banner_dev_days_attendee',
    },
  ]
}
